import { Link } from "gatsby";
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import React, { ReactNode, useState } from "react";
import {
  detailSmall,
  heading150,
  heading400,
  paragraphTextLarge,
} from "../../../stitches/combinedStyles";
import { styled } from "../../../stitches/stitches.config";
import Button from "../Button/Button";
import HideOnScreen from "../HideOnScreen";
import { HideOnScreenVariants, HideOnScreenBreakpoints } from "../HideOnScreen";
import lightsGif from "../../../images/benchtop_lights.gif";

const desktopButtonDemo = `Book demo`;

interface HeroProps {
  title: ReactNode;
  byline?: ReactNode;
  bylineMaxWidth?: number;
  hideBylineOnType?: HideOnScreenVariants;
  hideBylineOnBreakpoint?: HideOnScreenBreakpoints;
  secondaryButton: {
    text: ReactNode;
    path: string;
  };
  image: IGatsbyImageData | undefined;
  mobileImage?: IGatsbyImageData | undefined;
  bg?: IGatsbyImageData | undefined;
  light?: boolean;
  page?: string;
}

const imageAlt =
  "PLX-Benchtop - stress/strain curves from indentation mechanical tests";

const Hero = ({
  title,
  byline,
  bylineMaxWidth,
  hideBylineOnType,
  hideBylineOnBreakpoint = "md",
  secondaryButton,
  image,
  mobileImage,
  bg,
  light,
  page
}: HeroProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const bylineArray = React.Children.toArray(byline);
  return (
    <Container className={`hero-${page}`}>
      <TopContentContainer className={`topContentContainer-${page}`}>
        <TextContent className={page}>
          <Title>{title}</Title>
          {byline && (
            <HideOnScreen
              variant={hideBylineOnType}
              breakpoint={hideBylineOnBreakpoint}
            >
              {bylineArray.map((text, i) => (
                <Byline key={i} style={{ maxWidth: bylineMaxWidth }}>
                  {text}
                </Byline>
              ))}
            </HideOnScreen>
          )}
          <DesktopButtons>
            {(page === "hotpip") ? <>
              <a href="https://plastometrex-6034553.hs-sites.com/hot-pip-updates">
                <Button size="medium" tier="primary">
                Stay up to date
                </Button>
              </a>
              {/* <Link to={secondaryButton.path}>
                <Button size="medium" tier="secondary" arrow>
                  {secondaryButton.text}
                </Button>
              </Link> */}
            </>
              :
              <>
                <Link to="/book-a-demo">
                  <Button size="medium" tier="primary">
                    {desktopButtonDemo}
                  </Button>
                </Link>
                <Link to={secondaryButton.path}>
                  <Button size="medium" tier="secondary" arrow>
                    {secondaryButton.text}
                  </Button>
                </Link>
              </>}
          </DesktopButtons>
        </TextContent>
        <RenderWrapper className={page}>
          {bg && (
            <BackgroundImage image={bg} alt="background" loading="eager" className={page} />
          )}
          {image && !mobileImage && (
            <BenchtopImage
              image={image}
              //objectFit="cover"
              onLoad={() => {
                setTimeout(() => setImageLoaded(true), 1000);
              }}
              alt={imageAlt}
              loading="eager"
              className={page}
            />
          )}
          {image && mobileImage && (
            <>
              <DesktopContainer>
                <BenchtopImage
                  image={image}
                  objectFit="cover"
                  onLoad={() => {
                    setTimeout(() => setImageLoaded(true), 1000);
                  }}
                  alt={imageAlt}
                  loading="eager"
                  className={page}
                />
              </DesktopContainer>
              <MobileContainer>
                <MobileBenchtopImage
                  image={mobileImage}
                  objectFit="cover"
                  alt={imageAlt}
                  loading="eager"
                  className={page}
                />
              </MobileContainer>
            </>
          )}


          {light && (<LightsImage src={lightsGif} transparent={!imageLoaded} />)}
        </RenderWrapper>
      </TopContentContainer>
    </Container>
  );
};

export default Hero;

const Container = styled("div", {
  maxWidth: 1400,
  margin: "auto",
  marginBottom: 32,
  position: "relative",
  overflow: "hidden",
  "@md": {
    backgroundColor: "#F9F9F9",
    height: 600,
    maxHeight: "62vw",
    marginBottom: 0,
  },
  "@lg": {
    marginBottom: 50,
    borderRadius: 8,
    width: "calc(100% - 64px)",
  },
});

const TopContentContainer = styled("div", {
  width: "90%",
  margin: "auto",
  maxWidth: 1150,
   "&.topContentContainer-hotpip": { width: "100%",maxWidth: "100%", },
  "@md": {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 50,
    height: "100%",
  },
});

const TextContent = styled("div", {
  display: "flex",
  flexDirection: "column",
  zIndex: 3,
  marginBottom: 20,
   "&.hotpip": { paddingLeft:30, },
  "@md": {
    width: "55%",
    minWidth: "527px",
     "&.hotpip": { paddingLeft:80, },
  },
  "@lg": {
    width: "55%",
    //maxWidth: 560,
     "&.hotpip": { paddingLeft:80, },
  },
});
const Title = styled("h1", {
  ...heading150,
  fontSize: 26,
  lineHeight: "38px",
  letterSpacing: "-1.5px",
  marginBottom: "0px",
  "@md": {
    ...heading400,
    fontWeight: 700,
    lineHeight: "51px",
    letterSpacing: "-1.5px",
    marginBottom: "20px",
  },
  "@lg": {
    ...heading400,
    fontWeight: 700,
  },
});
const Byline = styled("p", {
  ...detailSmall,
  maxWidth: 468,
  marginBottom: 10,
  marginLeft: "auto",
  marginRight: "auto",
  "@md": {
    ...paragraphTextLarge,
    marginBottom: 32,
    marginLeft: 0,
    marginRight: 0,
  },
});

const RenderWrapper = styled("div", {
  position: "relative",
  //backgroundColor: "#F9F9F9",
  //overflow: "hidden",
  //height: "50vw",
  //minHeight: 250,
  textAlign: "center",
  //"&.hotpip": { height: "initial", },
  "@md": {
    position: "static",
    height: "auto",

  },
});

const BackgroundImage = styled(GatsbyImage, {
  "&.gatsby-image-wrapper": {
    position: "absolute",
    right: "-60%",
    bottom: 0,
    width: "160%",
    "&.gatsby-image-wrapper.hotpip": { display: "none" },
    "@md": {
      right: 0,
      maxWidth: "68%",
      "&.gatsby-image-wrapper.hotpip": { display: "block" },
    },
  },
});

const imageBase = {
  //height: "100%",
  //position: "absolute" as const,
  "&.gatsby-image-wrapper.hotpip": {},
  "&.gatsby-image-wrapper": {
    //height: "100%",
    //position: "absolute" as const,
  },
  //bottom: -10,
  //right: "50%",
  //transform: "translateX(50%)",
  //width: "70%",
  //minWidth: 280,
  //zIndex: 2,
  "@md": {
    //bottom: 0,
    //right: 0,
    //height: "auto",
    "&.gatsby-image-wrapper": {
      //height: "auto",
    },
    //transform: "translate(25%, 17%)",
    //width: 800,
    //maxWidth: "78%",
  },
};

const BenchtopImage = styled(GatsbyImage, {
  ...imageBase,
  "&.hotpip": {
    //transform: "scale(1)",
    "@md": {
      //transform: "scale(0.81)translate(25%, 17%)",
    },
    "@lg": {
      //transform: "scale(1)",
    }
  },
});

const MobileBenchtopImage = styled(GatsbyImage, {
  position: "static",
});

const LightsImage = styled("img", {
  ...imageBase,
  objectFit: "cover",
  transition: "opacity 100ms linear 0ms",
  variants: {
    transparent: {
      true: {
        opacity: 0,
      },
    },
  },
});

const DesktopButtons = styled("div", {
  display: "none",
  "@md": { display: "flex" },
});

const DesktopContainer = styled("div", {
  display: "none",
  "@md": {
    display: "block",
  },
})

const MobileContainer = styled("div", {
  display: "block",
  "@md": {
    display: "none",
  },
})